// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    Divider
} from '@mui/material';
import { ManageAccounts, Cancel } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useState, useEffect  } from 'react';
import { deepPurple, grey } from '@mui/material/colors';
import useFetchData from 'hooks/useFetchData';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';

import { useTranslation } from "react-i18next";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const UpdateAmbassadorLevelModal = ({
    updateAmbassadorLevelModalOpen,
    setUpdateAmbassadorLevelModalOpen,
    ambassadorLevelRow,
    fetchAmbassadorLevels
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [ambassadorLevelName, setAmbassadorLevelName] = useState(ambassadorLevelRow.ambassadorLevelName);
    const [cashBonusGiven, setCashBonusGiven] = useState(ambassadorLevelRow.cashBonusGiven);
    const [incentivePercentage, setIncentivePercentage] = useState(ambassadorLevelRow.incentivePercentage);
    const [eachSetTaskNumber, setEachSetTaskNumber] = useState(ambassadorLevelRow.eachSetTaskNumber);
    const [totalTaskSet, setTotalTaskSet] = useState(ambassadorLevelRow.totalTaskSet);
    const [comboTaskIncentivePercentage, setComboTaskIncentivePercentage] = useState(ambassadorLevelRow.comboTaskIncentivePercentage);
    const [taskPriceRangeFrom, setTaskPriceRangeFrom] = useState(ambassadorLevelRow.taskPriceRangeFrom);
    const [taskPriceRangeTo, setTaskPriceRangeTo] = useState(ambassadorLevelRow.taskPriceRangeTo);
    const [minWithdrawalAmount, setMinWithdrawalAmount] = useState(ambassadorLevelRow.minWithdrawalAmount);
    const [maxWithdrawalAmount, setMaxWithdrawalAmount] = useState(ambassadorLevelRow.maxWithdrawalAmount);
    const [requiredTaskCountToWithdraw, setRequiredTaskCountToWithdraw] = useState(ambassadorLevelRow.requiredTaskCountToWithdraw);
    const [withdrawalFees, setWithdrawalFees] = useState(ambassadorLevelRow.withdrawalFees);

    const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false);
    const submitUpdateAmbassadorProgramDetail = async () => {
        setUpdateButtonDisabled(true);

        try {
            const res = await axiosInstance.put(
                '/AmbassadorProgramDetail',
                {
                    ambassadorLevelID: ambassadorLevelRow.ambassadorLevel,
                    incentivePercentage: incentivePercentage,
                    eachSetTaskNumber: eachSetTaskNumber,
                    totalTaskSet: totalTaskSet,
                    comboTaskIncentivePercentage: comboTaskIncentivePercentage,
                    taskPriceRangeFrom: taskPriceRangeFrom,
                    taskPriceRangeTo: taskPriceRangeTo,
                    minWithdrawalAmount: minWithdrawalAmount,
                    maxWithdrawalAmount: maxWithdrawalAmount,
                    requiredTaskCountToWithdraw: requiredTaskCountToWithdraw,
                    withdrawalFees: withdrawalFees,
                    ambassadorLevelName: ambassadorLevelName,
                    cashBonusGiven: cashBonusGiven
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (res.status === 200) {
                successAlert('成功更新会员等级');
                fetchAmbassadorLevels();
                setUpdateAmbassadorLevelModalOpen(false);
            } else {
                errorAlert('发生错误');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setUpdateButtonDisabled(false);
        }
    };

    useEffect(() => {
        if (ambassadorLevelRow) {
            setAmbassadorLevelName(ambassadorLevelRow.ambassadorLevelName || '');
            setCashBonusGiven(ambassadorLevelRow.cashBonusGiven || 0);
            setIncentivePercentage(ambassadorLevelRow.incentivePercentage || 0);
            setEachSetTaskNumber(ambassadorLevelRow.eachSetTaskNumber || 0);
            setTotalTaskSet(ambassadorLevelRow.totalTaskSet || 0);
            setComboTaskIncentivePercentage(ambassadorLevelRow.comboTaskIncentivePercentage || 0);
            setTaskPriceRangeFrom(ambassadorLevelRow.taskPriceRangeFrom || 0);
            setTaskPriceRangeTo(ambassadorLevelRow.taskPriceRangeTo || 0);
            setMinWithdrawalAmount(ambassadorLevelRow.minWithdrawalAmount || 0);
            setMaxWithdrawalAmount(ambassadorLevelRow.maxWithdrawalAmount || 0);
            setRequiredTaskCountToWithdraw(ambassadorLevelRow.requiredTaskCountToWithdraw || 0);
            setWithdrawalFees(ambassadorLevelRow.withdrawalFees || 0);
        }
    }, [ambassadorLevelRow]);

    return (
        <>
            <Modal
                open={updateAmbassadorLevelModalOpen}
                onClose={() => setUpdateAmbassadorLevelModalOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography id="modal-modal-title" variant="h3" component="h2">
                            {t("ambassadorLevel.editVIPLevel")}
                        </Typography>
                        <Cancel onClick={() => setUpdateAmbassadorLevelModalOpen(false)} sx={{ width: '30px', height: '30px' }} />
                    </Box>
                    <Box
                        sx={{
                            maxHeight: '80vh',
                            overflowY: 'auto',
                            paddingRight: '10px'
                        }}
                    >
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bolder', marginTop: '20px' }}>
                                {t("ambassadorLevel.personalDetails")}
                            </Box>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='ambassadorLevelName1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.vipLevelName")}
                                    </Box>
                                </Grid>
                                <Grid key='ambassadorLevelName2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="ambassadorLevelName"
                                        type="text"
                                        fullWidth
                                        variant="outlined"
                                        value={ambassadorLevelName}
                                        onChange={(e) => setAmbassadorLevelName(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='cashBonusGiven1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.minAmount")}
                                    </Box>
                                </Grid>
                                <Grid key='cashBonusGiven2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="cashBonusGiven"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={cashBonusGiven}
                                        onChange={(e) => setCashBonusGiven(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Divider style={{marginTop: '20px', marginBottom: '20px'}} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bolder', marginTop: '20px' }}>
                                {t("ambassadorLevel.taskDetails")}
                            </Box>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='eachSetTaskNumber1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.taskCountPerDay")}
                                    </Box>
                                </Grid>
                                <Grid key='eachSetTaskNumber2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="eachSetTaskNumber"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={eachSetTaskNumber}
                                        onChange={(e) => setEachSetTaskNumber(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='totalTaskSet1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.taskSetPerDay")}
                                    </Box>
                                </Grid>
                                <Grid key='totalTaskSet2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="totalTaskSet"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={totalTaskSet}
                                        onChange={(e) => setTotalTaskSet(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='productRange1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.productRange")}
                                    </Box>
                                </Grid>
                                <Grid key='productRange2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="taskPriceRangeFrom"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        variant="outlined"
                                        value={taskPriceRangeFrom}
                                        onChange={(e) => setTaskPriceRangeFrom(e.target.value)}
                                    />
                                    {t("ambassadorLevel.to")}
                                    <TextField
                                        required
                                        name="taskPriceRangeTo"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        variant="outlined"
                                        value={taskPriceRangeTo}
                                        onChange={(e) => setTaskPriceRangeTo(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='incentivePercentage1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.comissionPercentage")}
                                    </Box>
                                </Grid>
                                <Grid key='incentivePercentage2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="incentivePercentage"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={incentivePercentage}
                                        onChange={(e) => setIncentivePercentage(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='comboTaskIncentivePercentage1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.comboComissionPercentage")}
                                    </Box>
                                </Grid>
                                <Grid key='comboTaskIncentivePercentage2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="comboTaskIncentivePercentage"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={comboTaskIncentivePercentage}
                                        onChange={(e) => setComboTaskIncentivePercentage(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Divider style={{marginTop: '20px', marginBottom: '20px'}} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: 'bolder', marginTop: '20px' }}>
                                {t("ambassadorLevel.withdrawalDetails")}
                            </Box>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='minWithdrawalAmount1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.minWithdrawalAmount")}
                                    </Box>
                                </Grid>
                                <Grid key='minWithdrawalAmount2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="minWithdrawalAmount"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={minWithdrawalAmount}
                                        onChange={(e) => setMinWithdrawalAmount(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='maxWithdrawalAmount1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.maxWithdrawalAmount")}
                                    </Box>
                                </Grid>
                                <Grid key='maxWithdrawalAmount2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="maxWithdrawalAmount"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={maxWithdrawalAmount}
                                        onChange={(e) => setMaxWithdrawalAmount(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='requiredTaskCountToWithdraw1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.completedTaskPerDayToWithdraw")}
                                    </Box>
                                </Grid>
                                <Grid key='requiredTaskCountToWithdraw2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="requiredTaskCountToWithdraw"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={requiredTaskCountToWithdraw}
                                        onChange={(e) => setRequiredTaskCountToWithdraw(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Grid item container pr={{ md: '5px' }}>
                                <Grid key='withdrawalFees1' xs={5} sx={{ mt: 2 }} style={{alignContent: 'center'}}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {t("ambassadorLevel.withdrawalFees")}
                                    </Box>
                                </Grid>
                                <Grid key='withdrawalFees2' xs={7} sx={{ mt: 2 }}>
                                    <TextField
                                        required
                                        name="withdrawalFees"
                                        type="number"
                                        onWheel={(e) => e.target.blur()}
                                        fullWidth
                                        variant="outlined"
                                        value={withdrawalFees}
                                        onChange={(e) => setWithdrawalFees(e.target.value)}
                                    />
                                </Grid>
                            </Grid>
                            <Button
                                variant="contained"
                                sx={{
                                    display: 'block',
                                    mx: 'auto',
                                    width: '150px',
                                    backgroundColor: deepPurple[500],
                                    marginTop: '30px'
                                }}
                                onClick={submitUpdateAmbassadorProgramDetail}
                                disabled={updateButtonDisabled}
                            >
                                {t("ambassadorLevel.confirmUpdateVIPLevel")}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default UpdateAmbassadorLevelModal;